import { useState } from 'react';
import { Alert, Badge, Group, Paper, SimpleGrid, Stack, Table, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import { CsvDownloadButton } from '../components/CsvDownloadButton';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { SiteFilterSelect } from '../components/FilterSelects';
import { ReportPage } from '../components/ReportPage';
import { rangeQuery, useMassBalanceReport } from '../api';
import { formatKg, formatQty, formatTonnesApprox } from '../format';

/** A residual within this tolerance (kg) is treated as "explained" rounding, not a
 * real unexplained loss — the identity is exact arithmetic, but display needs a
 * threshold so a 0.001kg rounding artifact doesn't read as an alarm. */
const RESIDUAL_TOLERANCE_KG = 1;

function ComponentRow({ label, dryKg, sign }: { label: string; dryKg: string; sign?: '+' | '-' }) {
  // Components may legitimately be NEGATIVE when in-period reversals of pre-period
  // movements dominate the period (round-3 gate 2026-07-07) — so outbound rows
  // ('-') render their signed CONTRIBUTION via numeric negation (a literal '-'
  // prefix would show '--x' on a negative component), and inbound rows ('+') only
  // show the '+' when the value is actually non-negative. `|| 0` normalises the -0
  // a negated zero produces.
  const value = sign === '-' ? String(-Number(dryKg) || 0) : dryKg;
  const showPlus = sign === '+' && Number(dryKg) >= 0;
  return (
    <Table.Tr>
      <Table.Td>{label}</Table.Td>
      <Table.Td style={{ textAlign: 'right' }}>
        {showPlus ? '+' : ''}
        {formatKg(value)}
      </Table.Td>
      <Table.Td style={{ textAlign: 'right' }}>
        <Text size="xs" c="dimmed">
          {formatTonnesApprox(value)}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
}

/** Report #3 (BUILD_SPEC.md §9.6, §13) — THE key correctness report. Requires
 * site + from + to (the query schema has no defaults for these — an unscoped mass
 * balance isn't a meaningful identity). */
export function MassBalanceReport() {
  const [siteId, setSiteId] = useState<string | null>(null);
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);

  const canRun = Boolean(siteId && from && to);
  const query = useMassBalanceReport({ siteId, from, to });
  const data = query.data;

  const residual = data ? Number(data.residualDryKg) : 0;
  const residualSevere = Math.abs(residual) > RESIDUAL_TOLERANCE_KG;

  return (
    <ReportPage
      title="Mass balance"
      description="Site x period dry-mass identity: opening + receipts + production in - production out - dispatches - issues +/- adjustments = closing. Residual is unexplained loss."
      action={
        canRun ? (
          <CsvDownloadButton
            path="/reporting/mass-balance"
            query={{ siteId: siteId ?? undefined, ...rangeQuery(from, to) }}
            filename="mass-balance.csv"
            description="Exports this site's mass-balance components for the selected period as a CSV file"
          />
        ) : undefined
      }
    >
      <Group gap="md" wrap="wrap" align="flex-end">
        <SiteFilterSelect value={siteId} onChange={setSiteId} label="Site" placeholder="Choose a site" w={220} />
        <DateRangeFilter from={from} to={to} onFromChange={setFrom} onToChange={setTo} required />
      </Group>

      {!canRun && (
        <Alert color="graphite" variant="light" icon={<IconInfoCircle size={16} />}>
          Choose a site and both a from/to date to run the mass balance.
        </Alert>
      )}

      {canRun && query.isLoading && <Text c="dimmed">Loading mass balance…</Text>}

      {canRun && query.isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load the mass balance for the selected period.
        </Alert>
      )}

      {data && (
        <Stack gap="lg">
          <Paper withBorder radius="md" p="md">
            <Table verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Component</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Dry kg</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}> </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <ComponentRow label="Opening" dryKg={data.components.openingDryKg} />
                <ComponentRow label="Receipts" dryKg={data.components.receiptsDryKg} sign="+" />
                <ComponentRow label="Production in" dryKg={data.components.productionInDryKg} sign="+" />
                <ComponentRow label="Production out" dryKg={data.components.productionOutDryKg} sign="-" />
                <ComponentRow label="Pack in" dryKg={data.components.packInDryKg} sign="+" />
                <ComponentRow label="Pack out" dryKg={data.components.packOutDryKg} sign="-" />
                <ComponentRow label="Returns" dryKg={data.components.returnsDryKg} sign="+" />
                <ComponentRow label="Dispatches" dryKg={data.components.dispatchesDryKg} sign="-" />
                <ComponentRow label="Issues" dryKg={data.components.issuesDryKg} sign="-" />
                <ComponentRow label="Adjustments" dryKg={data.components.adjustmentsDryKg} />
                <Table.Tr>
                  <Table.Td>
                    <strong>Expected closing</strong>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <strong>{formatKg(data.expectedClosingDryKg)}</strong>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Text size="xs" c="dimmed">
                      {formatTonnesApprox(data.expectedClosingDryKg)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
                <ComponentRow label="Actual closing" dryKg={data.components.closingDryKg} />
                <Table.Tr>
                  <Table.Td>
                    <strong>Residual (unexplained)</strong>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Badge color={residualSevere ? 'red' : 'green'} variant="light" size="lg">
                      {formatKg(data.residualDryKg)}
                    </Badge>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Text size="xs" c="dimmed">
                      {formatTonnesApprox(data.residualDryKg)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Paper>

          {data.adjustmentsByReason.length > 0 && (
            <Stack gap="xs">
              <Title order={4}>Adjustments by reason</Title>
              <Text size="xs" c="dimmed">
                "Process loss" vs "measurement correction" stay distinguishable here, per §9.6.
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm">
                {data.adjustmentsByReason.map((a) => (
                  <Paper key={a.reason} withBorder radius="md" p="sm">
                    <Text size="sm" c="dimmed">
                      {a.reason}
                    </Text>
                    <Text fw={600}>{formatKg(a.dryKg)}</Text>
                  </Paper>
                ))}
              </SimpleGrid>
            </Stack>
          )}

          {data.byStage.length > 0 && (
            <Stack gap="xs">
              <Title order={4}>By stage (drill-down)</Title>
              <Table.ScrollContainer minWidth={480}>
                <Table verticalSpacing="xs" highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Stage</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>Production in (dry kg)</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>Production out (dry kg)</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.byStage.map((s) => (
                      <Table.Tr key={s.stageId}>
                        <Table.Td>{s.stageName}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{formatKg(s.productionInDryKg)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{formatKg(s.productionOutDryKg)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Stack>
          )}

          {data.packThroughput.length > 0 && (
            <Stack gap="xs">
              <Title order={4}>Pack/EACH throughput</Title>
              <Text size="xs" c="dimmed">
                Net counts by product x movement-type for EACH-denominated products (§9.6/§13, ruling R2) — never summed into
                the dry-mass identity above. Signed: outbound types read negative, and reversals net against their originals.
              </Text>
              <Table.ScrollContainer minWidth={480}>
                <Table verticalSpacing="xs" highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Product</Table.Th>
                      <Table.Th>Movement type</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>Net qty (count)</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.packThroughput.map((p) => (
                      <Table.Tr key={`${p.productId}-${p.type}`}>
                        <Table.Td>
                          {p.productSku} — {p.productName}
                        </Table.Td>
                        <Table.Td>{p.type}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{formatQty(p.netQty)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Stack>
          )}
        </Stack>
      )}
    </ReportPage>
  );
}
